import axios from 'axios'

// 从环境变量获取API基础URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

// API配置管理
export interface ApiConfig {
  // 基础配置
  baseURL: string
  timeout: number
  
  // 重试配置
  enableAutoRetry: boolean
  maxRetries: number
  retryDelay: number
  
  // Token配置
  tokenKey: string
  refreshTokenKey: string
  
  // 调试配置
  enableDebug: boolean
  logRequests: boolean
  logResponses: boolean
}

// 默认配置
export const defaultApiConfig: ApiConfig = {
  baseURL: API_BASE_URL,
  timeout: 90000,
  
  // 默认启用自动重试，但可以动态控制
  enableAutoRetry: false,
  maxRetries: 1,
  retryDelay: 10000,
  
  tokenKey: 'apiToken',
  refreshTokenKey: 'apiToken_refresh',
  
  enableDebug: import.meta.env.DEV,
  logRequests: false,
  logResponses: false
}

// 配置管理器
class ApiConfigManager {
  private config: ApiConfig = { ...defaultApiConfig }
  
  // 获取当前配置
  getConfig(): ApiConfig {
    return { ...this.config }
  }
  
  // 更新配置
  updateConfig(newConfig: Partial<ApiConfig>): void {
    this.config = { ...this.config, ...newConfig }
  }
  
  // 重置为默认配置
  resetToDefault(): void {
    this.config = { ...defaultApiConfig }
  }
  
  // 启用自动重试
  enableAutoRetry(): void {
    this.config.enableAutoRetry = true
  }
  
  // 禁用自动重试
  disableAutoRetry(): void {
    this.config.enableAutoRetry = false
  }
  
  // 设置重试参数
  setRetryConfig(maxRetries: number, retryDelay: number): void {
    this.config.maxRetries = maxRetries
    this.config.retryDelay = retryDelay
  }
  
  // 设置超时时间
  setTimeout(timeout: number): void {
    this.config.timeout = timeout
  }
  
  // 启用调试模式
  enableDebug(): void {
    this.config.enableDebug = true
    this.config.logRequests = true
    this.config.logResponses = true
  }
  
  // 禁用调试模式
  disableDebug(): void {
    this.config.enableDebug = false
    this.config.logRequests = false
    this.config.logResponses = false
  }
}

// 创建全局配置管理器实例
export const apiConfigManager = new ApiConfigManager()

// 导出默认配置，方便直接使用
export { defaultApiConfig as apiConfig }

// 创建axios实例
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// 创建平台API客户端
export const platformApiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// 请求拦截器
apiClient.interceptors.request.use(
  (config) => {
    // 从localStorage获取token
    const token = localStorage.getItem('apiToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器
apiClient.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    if (error.response?.status === 401) {
      // 未授权，清除token并跳转到登录页（仅在生产环境）
      if (import.meta.env.PROD) {
        localStorage.removeItem('apiToken')
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

// 平台API的请求拦截器
platformApiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('apiToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 平台API的响应拦截器
platformApiClient.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    if (error.response?.status === 401) {
      // 在开发环境中，跳过清除操作以保持localStorage数据
      if (import.meta.env.PROD) {
        localStorage.removeItem('apiToken')
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

export default {
  API_BASE_URL,
}